import { onUnmounted, ref, shallowRef, watch } from 'vue'

/**
 * Poll in place without overlapping requests or letting an old route overwrite the current one. The clock
 * (`now`) ticks every second; `everyMs` spaces the reads out for a resource that is costly to build and
 * slow to change, counted from the last read (a manual `refresh` included).
 *
 * Each read is handed what the last one returned for the same key (`null` for the first, and again after the
 * key changes), so a resource that only grows can ask for what is new and fold it into what it has. `active`
 * is asked on each tick: while it says no, neither the clock nor a return to the tab reads — for a resource
 * that has stopped changing, such as a finished run. A new key and a manual `refresh` still read.
 */
export function useLiveResource<T>(
    key: () => string,
    fetchResource: (signal: AbortSignal, current: T | null) => Promise<T>,
    { everyMs = 1000, active = () => true }: { everyMs?: number; active?: () => boolean } = {},
) {
    const data = shallowRef<T | null>(null)
    const error = shallowRef<Error | null>(null)
    const loading = ref(true)
    const refreshing = ref(false)
    const updatedAt = ref<number | null>(null)
    const now = ref(Date.now())
    const visible = ref(document.visibilityState !== 'hidden')
    let request: AbortController | null = null
    let disposed = false
    const everyTicks = Math.max(1, Math.round(everyMs / 1000))
    let ticksSinceRead = 0

    async function refresh() {
        if (disposed || request) return
        ticksSinceRead = 0
        const current = new AbortController()
        request = current
        refreshing.value = true
        loading.value = data.value === null
        try {
            const result = await fetchResource(current.signal, data.value)
            if (disposed || request !== current || current.signal.aborted) return
            data.value = result
            updatedAt.value = Date.now()
            error.value = null
        } catch (caught) {
            if (disposed || request !== current || current.signal.aborted) return
            error.value = caught instanceof Error ? caught : new Error('Request failed')
        } finally {
            if (request === current) {
                request = null
                refreshing.value = false
                loading.value = false
            }
        }
    }

    /**
     * Read again now, dropping a read already in flight rather than waiting on it: for a caller that has just changed
     * what the resource holds, so an answer read before the change can never land after it. `refresh` leaves a read
     * in flight to land, which is right for a poll and for a person asking again.
     */
    function reread(): Promise<void> {
        request?.abort()
        request = null
        return refresh()
    }

    watch(
        key,
        () => {
            request?.abort()
            request = null
            data.value = null
            error.value = null
            updatedAt.value = null
            void refresh()
        },
        { immediate: true },
    )

    const timer = setInterval(() => {
        now.value = Date.now()
        ticksSinceRead += 1
        if (visible.value && ticksSinceRead >= everyTicks && active()) void refresh()
    }, 1000)

    function visibilityChanged() {
        visible.value = document.visibilityState !== 'hidden'
        if (visible.value && active()) void refresh()
    }
    document.addEventListener('visibilitychange', visibilityChanged)
    onUnmounted(() => {
        disposed = true
        request?.abort()
        request = null
        clearInterval(timer)
        document.removeEventListener('visibilitychange', visibilityChanged)
    })
    return { data, error, loading, refreshing, updatedAt, now, visible, refresh, reread }
}
